import { CalendarIcon, CheckCircle, DatabaseBackup, DatabaseBackupIcon, Download, HardDrive, Loader2, RefreshCw, Share, Timer, XCircle } from "lucide-react";
import { Layout } from "./Layout";
import { StatCard } from "./StatCard";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "../components/ui/button";
import { BreadcrumbHeader } from "./BreadCrumbNavigation";
import { AppContext } from "@/App";
import { useContext, useEffect, useState } from "react";
import { DataTable } from "./DataTable";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { endOfToday, endOfYesterday, format, startOfToday, startOfYesterday, subDays } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { apiClient } from "@/Utils/apiClient";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { buildQuery, errorResponse, getLastWeekRange, toSQLDateTime } from "@/Utils/helpers";
import { PaginationCustom } from "./ui/pagination-custom";
import { downloadAsXlsx } from "./ui/downloadHelpers";
interface Backup {
    type: string;
    backupDateAndTime: string | Date;
    size: string;
    status: string;
    location: string
    updatedAt: string;
    createdAt: string
    id: string
}
interface BackupData {
    data: Backup[],
    pagination: {
        totalPages: number;
        totalCount: number;
        limit: number;
        page: number;
    }
}
export function BackUpManagement() {
    const { setCurrentView } = useContext(AppContext);
    const { token } = useAuth()
    const [tempRange, setTempRange] = useState({
        from: startOfYesterday(),
        to: endOfToday()
    })
    const [filters, setFilters] = useState({

        from: startOfYesterday(),
        to: endOfToday(),
        // type: '',
        status: ''

    })
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [data, setData] = useState<BackupData>()
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 15;
    const [isDownloading, setIsDownloading] = useState(false)
    // const dateRangeValue=[
    //     {
    //         label:"Today",
    //         value:{
    //             from:startOfToday(),
    //             to:endOfToday()
    //         }
    //     },
    //     {
    //       label:"Yesterday",
    //       value:{
    //         from:startOfYesterday() ,
    //         to:endOfYesterday()

    //       }
    //     },
    //     {
    //       label:"Last Week",
    //       value:getLastWeekRange()
    //     }
    //   ]

    const fetchData = async () => {
        try {
            const queryparams = {
                from: filters.from.toLocaleDateString("en-CA"),
                to: filters.to.toLocaleDateString("en-CA"),
                status: filters.status,
                page: currentPage,
                limit: itemsPerPage

            }
            const query = buildQuery(queryparams, {
                from: "fromDate",
                to: "toDate",
                page: "page",
                limit: "limit",
                status: "status",
            })
            const response = await apiClient(`backup/summary?${query}`, {
                method: "GET",
                token

            })
            const result = await response.json();
            if (!result.success) {
                throw new Error(result.error)
            }
            setData(result);
            // toast.success("Backup List fetched ")
        }
        catch (err) {
            const msg = errorResponse(err);
            toast.error(msg);
        }

    }
    useEffect(() => {
        fetchData();
        setCurrentPage(1)
    }, [currentPage, filters.from, filters.to, filters.status, filters.type])
    // pagination
    const totalPages = data?.pagination?.totalPages || 0;
    const totalItems = data?.pagination?.totalCount || 0
    const onPageChange = (page: number) => {
        setCurrentPage(page);
    };
    // download single item
    const handleDownloadItem = async (detail: Backup) => {
        try {
            const query = buildQuery({ download: true }, {
                download: "download"
            })
            const response = await apiClient(`backup/logs/${detail.id}?${query}`, {
                method: "GET",
                token
            })
            const getBackUpTime = `${new Date(detail.backupDateAndTime).toLocaleDateString()}_${new Date(detail.backupDateAndTime).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            })}`
            const fileName = `Backup_detail_of_${getBackUpTime}.xlsx`
            await downloadAsXlsx(response, fileName);

        }
        catch (err: any) {
            const errMsg = errorResponse(err);
            toast.error(errMsg);

        }
    }
    // download all Items listing
    const handleDownload = async () => {
        try {
            setIsDownloading(true)
            const queryparams = {
                from: filters.from.toLocaleDateString("en-CA"),
                to: filters.to.toLocaleDateString("en-CA"),
                status: filters.status,
                download: true

            }
            const query = buildQuery(queryparams, {
                from: "fromDate",
                to: "toDate",
                status: "status",
                download: "download"
            })
            const response = await apiClient(`backup/summary?${query}`, {
                method: "GET",
                token
            })
            const fileName = `Database_Backup_details.xlsx`
            await downloadAsXlsx(response, fileName);

        }
        catch (err: any) {
            const errMsg = errorResponse(err);
            toast.error(errMsg);

        }
        finally {
            setIsDownloading(false)
        }

    }
    //  clear filters
    const handleClearFilter = () => {
        setFilters({
            from: startOfYesterday(),
            to: endOfToday(),
            //   type: "",
            status: "",
        });
        setTempRange({
            from: startOfYesterday(),
            to: endOfToday()
        })
    }
    //   styles for table header and column
    const thStyle = "text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] w-40";
    const tdStyle = "font-inter text-sm text-gray-700"
    // get the style for status
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed': return 'bg-green-100 text-green-800 p-1 rounded text-sm';
            case 'Failed': return 'bg-red-100 text-red-800 p-1 rounded text-sm';
            default: return 'bg-gray-100 text-gray-800 p-1 rounded text-sm';
        }
    }
    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 p-6 ">
                <div className="space-y-6">
                    <div>
                        <BreadcrumbHeader
                            title="Backup Management Dashboard"
                            paragraph="Detail Summary of Backup Data"
                            className="space-y-2"
                            titleIcon={<DatabaseBackup className="w-6 h-6 text-blue-600" />}
                            titleClassName="font-inter font-semibold text-2xl leading-[100%] tracking-[0]"
                        // breadcrumbs={[
                        //     { label: "Backup Management", onClick: () => setCurrentView('backUp-management'), icon: <DatabaseBackup className="w-4 h-4" /> }

                        // ]}
                        />
                    </div>
                    {/* <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <StatCard
                                icon={<CheckCircle className="w-6 h-6" />}
                                value="Success"
                                label="Last Backup Status"
                                status="success"
                                layout="row"
                                width="w-68"
                                height="h-24"
                            />
                            <StatCard
                                icon={<Timer className="w-6 h-6" />}
                                value="22 Sept 2025, 07:35"
                                label="Last Backup Time"
                                layout="row"
                                width="w-68"
                                height="h-24"
                            />
                            <StatCard
                                icon={<HardDrive className="w-6 h-6" />}
                                value="12.3 GB"
                                label="Backup Size"
                                layout="row"
                                width="w-68"
                                height="h-24"
                            />
                            <StatCard
                                icon={<XCircle className="w-6 h-6" />}
                                value="12 minutes"
                                label="Backup Duration"
                                status="warning"
                                layout="row"
                                width="w-68"
                                height="h-24"
                            />
                            <StatCard
                                icon={<CalendarIcon className="w-6 h-6" />}
                                value="23 Sept 2025, 07:30"
                                label="Next Scheduled Backup"
                                layout="row"
                                width="w-68"
                                height="h-24"
                            />
                        </div>
                    </div> */}
                    <div>
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between">
                                    <div className=" text-md">Backup History</div>
                                    <div className="flex items-center gap-2">
                                        <div>
                                            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                                                <PopoverTrigger asChild>
                                                    <button className="bg-gray-100 rounded flex items-center h-9 px-2 gap-1">
                                                        <CalendarIcon className="w-4 h-4" />
                                                        <span className="text-xs text-gray-600 font-medium">
                                                            {filters.from && filters.to
                                                                ? `${format(filters.from, "MMM dd")} — ${format(filters.to, "MMM dd")}`
                                                                : "Select Date Range"}
                                                        </span>
                                                    </button>
                                                </PopoverTrigger>


                                                <PopoverContent
                                                    className="w-auto p-0 mx-4"
                                                    side="left"
                                                    align="start"
                                                    sideOffset={8}
                                                >
                                                    <div className="p-4">
                                                        <div className="text-xs font-medium mb-2">Select Date Range</div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <div className="text-xs text-muted-foreground">From</div>
                                                                <Calendar
                                                                    className="scale-90"   // shrink everything by 90%
                                                                    classNames={{
                                                                        day: "size-6 p-0 text-xs",      // smaller day cells
                                                                        head_cell: "w-6 text-[10px]",   // smaller header
                                                                        caption_label: "text-xs",       // smaller month label
                                                                    }}
                                                                    mode="single"
                                                                    selected={tempRange.from ?? undefined}
                                                                    onSelect={(date) => date && setTempRange((prev) => ({ ...prev, from: date }))}
                                                                    initialFocus
                                                                    disabled={{ after: new Date() }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <div className="text-xs text-muted-foreground">To</div>
                                                                <Calendar
                                                                    className="scale-90"   // shrink everything by 90%
                                                                    classNames={{
                                                                        day: "size-6 p-0 text-xs",      // smaller day cells
                                                                        head_cell: "w-6 text-[10px]",   // smaller header
                                                                        caption_label: "text-xs",       // smaller month label
                                                                    }}
                                                                    mode="single"
                                                                    selected={tempRange.to ?? undefined}
                                                                    onSelect={(date) => date && setTempRange((prev) => ({ ...prev, to: date }))}
                                                                    disabled={{ after: new Date() }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="flex justify-end gap-2 mt-4">
                                                            <Button variant="outline" size="sm" onClick={() => {
                                                                setShowDatePicker(false); 
                                                                setTempRange({
                                                                    from: startOfYesterday(),
                                                                    to: endOfToday()
                                                                })
                                                            }}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (tempRange.from > tempRange.to) {
                                                                        toast.error("To Date must be later than the From Date.")
                                                                        return;
                                                                    }
                                                                    setFilters(prev => ({
                                                                        ...prev,
                                                                        from: tempRange.from,
                                                                        to: tempRange.to
                                                                    }));
                                                                    setShowDatePicker(false);
                                                                }}
                                                            >
                                                                Apply
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                            {/* <div>
                  <div className="flex items-center gap-2 ">
                    <CalendarIcon className="w-5 h-5 text-gray-500" />
                    <Select value={
                      dateRangeValue.find(
                (range) =>
                  range?.value?.from.getTime() ===
                    filters.from.getTime() &&
                  range?.value?.to.getTime() ===
                    filters.to.getTime(),
              )?.label || "custom"
            }
                    onValueChange={(value) => { 
                      const range = dateRangeValue.find(
                  (r) => r.label === value,
                );
                if (range) {
                 setFilters(prev=>({...prev,from:range.value.from,to:range.value.to}))
                }; setCurrentPage(1); }}>
                      <SelectTrigger className="w-36">
                        <SelectValue  placeholder="select Date" />
                      </SelectTrigger>
                      <SelectContent>
                        {dateRangeValue.map((value,index)=>(
                          <SelectItem key={index} value={value?.label}>{value.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div> */}
                                        </div>
                                        <div>
                                            <Select value={filters.status} onValueChange={(e) => setFilters(prev => ({ ...prev, status: e }))}>
                                                <SelectTrigger className="w-34 bg-gray-100">
                                                    <SelectValue placeholder="All Status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {/* <SelectItem value="All">All Status</SelectItem> */}
                                                    <SelectItem value="completed">Completed</SelectItem>
                                                    <SelectItem value="failed">Failed</SelectItem>
                                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                                    {/* <SelectItem value="pending">Pending</SelectItem> */}

                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {/* <div>
                                            <Select value={filters.type} onValueChange={(e) => setFilters(prev => ({ ...prev, type: e }))}>
                                                <SelectTrigger className="w-34 bg-gray-100">
                                                    <SelectValue placeholder="All Type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="All">All Type</SelectItem> 
                                                    <SelectItem value="full">Full</SelectItem>
                                                    <SelectItem value="incremental">Incremental</SelectItem>
                                                    <SelectItem value="differential'">Differential</SelectItem>
                                                    <SelectItem value="transaction_log">Transaction Log</SelectItem>

                                                </SelectContent>
                                            </Select>
                                        </div> */}
                                        <div>
                                            <Button
                                                variant="outline"
                                                onClick={handleClearFilter}
                                                className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                                            >
                                                <RefreshCw className="w-4 h-4 mr-2 text-[#2C3E5D]" />
                                                Reset
                                            </Button>
                                        </div>
                                        <div><Button className="bg-gray-100 text-black hover:bg-gray-200" onClick={handleDownload}>{isDownloading ? (<>
                                            <Loader2 className=" h-4 w-4 animate-spin" />
                                            Exporting </>) : (
                                            <>
                                                <Share className="w-4 h-4" />
                                                Export
                                            </>
                                        )}</Button></div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <DataTable<Backup>
                                    data={data?.data || []}
                                    columns={[
                                        {
                                            key: "backupDateAndTime",
                                            header: "Date&Time",
                                            headerClassName: thStyle,
                                            cellClassName: tdStyle,
                                            render: (chart) => {
                                                const date = new Date(chart.backupDateAndTime);
                                                return (
                                                    <div className="flex flex-col ">
                                                        <span>{date.toLocaleDateString('en')}</span>
                                                        <span>
                                                            {date.toLocaleTimeString([], {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                hour12: true
                                                            })}
                                                        </span>
                                                    </div>
                                                );
                                            }

                                        },
                                        {
                                            key: "type",
                                            header: "Type",
                                            headerClassName: thStyle,
                                            cellClassName: tdStyle,
                                            render: (chart) => chart.type,
                                        },
                                        {
                                            key: "size",
                                            header: "Size",
                                            headerClassName: thStyle,
                                            cellClassName: tdStyle,
                                            render: (chart) => chart.size,
                                        },
                                        {
                                            key: "location",
                                            header: "Location",
                                            headerClassName: thStyle,
                                            cellClassName: tdStyle,
                                            render: (chart) => chart.location,
                                        },
                                        {
                                            key: "status",
                                            header: "Status",
                                            headerClassName: thStyle,
                                            cellClassName: tdStyle,
                                            render: (chart) => (<span className={getStatusColor(chart.status)}>{chart.status}</span>)
                                        },
                                        {
                                            key: "Action",
                                            header: "Action",
                                            headerClassName: thStyle,
                                            render: (chart) => (
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownloadItem(chart);


                                                        }}
                                                    // title="Download Details"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ),
                                        }
                                    ]}
                                />
                                {totalPages > 1 && (
                                    <div className="px-6 py-4 border-t bg-gray-50">
                                        <div className="flex justify-between items-center">
                                            {/* <span className="text-sm text-gray-600">
                                                                          Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                                                                        </span> */}

                                            <PaginationCustom
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                totalItems={totalItems}
                                                itemsPerPage={itemsPerPage}
                                                onPageChange={onPageChange}
                                                onItemsPerPageChange={(newItemsPerPage) => {
                                                    setCurrentPage(1) // Reset page to 1
                                                }}
                                            // showPageSizeSelector={false}  
                                            // showItemCount={false}         
                                            />
                                        </div>
                                    </div>



                                )}

                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

        </Layout>

    )

}